
export const numberFormat = new Intl.NumberFormat('es-CR', {
  maximumFractionDigits: 1,
});

export const preciseNumberFormat = new Intl.NumberFormat('es-CR', {
  maximumFractionDigits: 2,
});

export function formatHa(val) {
  if (val === null || val === undefined || val === '' || !Number.isFinite(Number(val))) return 'No disponible';
  const num = Number(val);
  return `${numberFormat.format(Math.round(num))} ha`;
}

export function formatPct(val) {
  if (val === null || val === undefined || val === '' || !Number.isFinite(Number(val))) return 'No disponible';
  const num = Number(val);
  return `${preciseNumberFormat.format(num)}%`;
}

export function normalizeKey(str) {
  if (!str) return '';
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function cleanFilename(name) {
  if (!name) return 'pina_costarica';
  return String(name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_\-]/g, '_')
    .toLowerCase();
}

export const YEARS = [2000, 2015, 2016, 2017, 2018, 2019];

export const GLOBAL_KPIS = {
  pina2000Ha: 13260.4,
  pina2019Ha: 82787.1,
  growthHa: 69526.7,
  growthPct: 524.3,
  annualGrowthHa: 3659.3,
  deforestationHa: 6370.0,
  deforestationPct: 7.7,
  pastureConversionHa: 3034.0,
  otherUsesHa: 60123.0,
  aspInvasionHa: 1930.6,
  aspInvasionCount: 7,
  activeCantonsCount: 15,
  totalCantonsCount: 84,
};

export const PROVINCES = [
  { name: 'Alajuela', areaHa: 49958.6, pct: 60.3, deforestacionHa: 3300.2 },
  { name: 'Limón', areaHa: 15066.7, pct: 18.2, deforestacionHa: 1120.4 },
  { name: 'Puntarenas', areaHa: 9094.4, pct: 11.0, deforestacionHa: 980.1 },
  { name: 'Heredia', areaHa: 7256.2, pct: 8.8, deforestacionHa: 720.5 },
  { name: 'San José', areaHa: 1411.2, pct: 1.7, deforestacionHa: 248.8 },
];

export const TOP_CANTONS = [
  'San Carlos',
  'Los Chiles',
  'Buenos Aires',
  'Pococí',
  'Río Cuarto',
  'Sarapiquí',
  'Upala',
  'Guácimo',
  'Siquirres',
  'Guatuso',
  'Pérez Zeledón',
  'Puntarenas',
  'Quepos',
  'Alajuela',
  'San Ramón',
];

export const HISTORICAL_SOURCES = {
  sentinel: {
    id: 'sentinel',
    name: 'Sentinel-2 Cloudless',
    shortName: 'Sentinel-2',
    resolution: '10 m',
    badge: '10 m · Óptico',
    description: 'Mosaicos globales anuales sin nubes de Copernicus Sentinel-2 (EOX)',
    years: [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025],
    defaultYear: 2025,
    labels: {
      2017: 'Mosaico Anual 2017 (10m)',
      2018: 'Mosaico Anual 2018 (10m)',
      2019: 'Mosaico Anual 2019 (10m)',
      2020: 'Mosaico Anual 2020 (10m)',
      2021: 'Mosaico Anual 2021 (10m)',
      2022: 'Mosaico Anual 2022 (10m)',
      2023: 'Mosaico Anual 2023 (10m)',
      2024: 'Mosaico Anual 2024 (10m)',
      2025: 'Mosaico Anual 2025 (10m)',
    },
    getTileUrl: (year) =>
      `https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-${year}_3857/default/g/{z}/{y}/{x}.jpg`,
    attribution: '© Sentinel-2 cloudless (EOX / Copernicus)',
    maxZoom: 16,
  },
  wayback: {
    id: 'wayback',
    name: 'Esri Wayback HD',
    shortName: 'Wayback HD',
    resolution: 'Submétrica',
    badge: 'HD · Submétrica',
    description: 'Mosaico satelital histórico de ultra alta resolución Esri World Imagery',
    years: [2014, 2017, 2020, 2022, 2024, 2026],
    defaultYear: 2026,
    labels: {
      2014: 'Wayback 2014 (Alta Resolución)',
      2017: 'Wayback 2017 (Alta Resolución)',
      2020: 'Wayback 2020 (Alta Resolución)',
      2022: 'Wayback 2022 (Alta Resolución)',
      2024: 'Wayback 2024 (Alta Resolución)',
      2026: 'Wayback 2026 (Alta Resolución)',
    },
    getTileUrl: () =>
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '© Esri, Maxar, Earthstar Geographics',
    maxZoom: 18,
  },
};

export const COLOR_SCALE = {
  pina2000: '#10b981', // Verde esmeralda base
  pina2019: '#d97706', // Ámbar dorado
  deforestacion: '#dc2626', // Rojo carmín
  asp: '#2563eb', // Azul zafiro áreas protegidas
  cantonBorder: '#94a3b8',
  cantonSelected: '#ea580c',
};

/**
 * Genera el libro Excel multihoja con todos los datos auditados
 */
/**
 * Genera el KML del cantón o polígono seleccionado para Google Earth
 */
export function generateCantonKML(feature) {
  if (!feature || !feature.geometry) return '';
  const p = feature.properties || {};
  const name = p.canton || p.name || 'Canton Costa Rica';
  const prov = p.provincia || '';
  const area = Math.round(Number(p.area_ha || 0));
  const pina19 = Math.round(Number(p.pina_2019 || 0));
  const defor = Math.round(Number(p.deforestacion_ha || 0));

  function coordsToKML(coords) {
    if (typeof coords[0] === 'number') {
      return `${coords[0]},${coords[1]},50 `;
    }
    return coords.map(coordsToKML).join('');
  }

  let geomXml = '';
  if (feature.geometry.type === 'Polygon') {
    const rings = feature.geometry.coordinates;
    const outer = rings[0].map((c) => `${c[0]},${c[1]},50`).join(' ');
    geomXml = `
      <Polygon>
        <extrude>1</extrude>
        <altitudeMode>relativeToGround</altitudeMode>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>${outer}</coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>`;
  } else if (feature.geometry.type === 'MultiPolygon') {
    geomXml = `<MultiGeometry>` + feature.geometry.coordinates.map((poly) => {
      const outer = poly[0].map((c) => `${c[0]},${c[1]},50`).join(' ');
      return `
        <Polygon>
          <extrude>1</extrude>
          <altitudeMode>relativeToGround</altitudeMode>
          <outerBoundaryIs>
            <LinearRing>
              <coordinates>${outer}</coordinates>
            </LinearRing>
          </outerBoundaryIs>
        </Polygon>`;
    }).join('') + `</MultiGeometry>`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${name} - Cantón de Costa Rica</name>
    <description>
      <![CDATA[
        <h3>Cantón: ${name}</h3>
        <p><b>Provincia:</b> ${prov}</p>
        <p><b>Superficie Cantonal:</b> ${area} ha</p>
        <p><b>Cultivo de Piña (2019):</b> ${pina19} ha</p>
        <p><b>Deforestación por Piña (2000-2019):</b> ${defor} ha</p>
        <hr/>
        <p>Fuente: Geoportal EIT · Monitoreo Agroambiental MOCUPP / CeNAT</p>
      ]]>
    </description>
    <Style id="pinaCantonStyle">
      <LineStyle>
        <color>ff00aaee</color>
        <width>3</width>
      </LineStyle>
      <PolyStyle>
        <color>4f00aaee</color>
      </PolyStyle>
    </Style>
    <Placemark>
      <name>${name}</name>
      <styleUrl>#pinaCantonStyle</styleUrl>
      ${geomXml}
    </Placemark>
  </Document>
</kml>`;
}

export function getGoogleEarthWebUrl(centroid) {
  if (!centroid || centroid.length < 2) {
    return 'https://earth.google.com/web/@10.4,-84.3,1000a,35y,0h,0t,0r';
  }
  const [lng, lat] = centroid;
  return `https://earth.google.com/web/@${lat.toFixed(6)},${lng.toFixed(6)},1500a,35y,0h,0t,0r`;
}

export function getFeatureCentroid(geometry) {
  if (!geometry) return [-84.2, 10.3];
  let sumX = 0;
  let sumY = 0;
  let count = 0;

  const traverse = (coords) => {
    if (typeof coords[0] === 'number') {
      sumX += coords[0];
      sumY += coords[1];
      count++;
      return;
    }
    coords.forEach(traverse);
  };
  traverse(geometry.coordinates);

  if (count === 0) return [-84.2, 10.3];
  return [sumX / count, sumY / count];
}
